// Loads third-party scripts only after the user has granted consent
// for the relevant category. Uses Google Consent Mode v2 defaults.

import { getConsent, onConsentChange, type StoredConsent } from "./consent";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    AccessibilityWidget?: { init: (opts: Record<string, unknown>) => void };
  }
}

const GA_ID = "G-S0RPZLFSD9";

let gaLoaded = false;
let widgetLoaded = false;
let consentModeInitialized = false;

function initConsentMode() {
  if (consentModeInitialized) return;
  consentModeInitialized = true;
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
  }
  // Default everything to denied (GDPR-safe baseline)
  window.gtag!("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "denied",
    personalization_storage: "denied",
    security_storage: "granted",
  });
  window.gtag!("js", new Date());
}

function loadGoogleAnalytics() {
  if (gaLoaded) return;
  gaLoaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
  window.gtag!("config", GA_ID, { anonymize_ip: true });
}

function loadAccessibilityWidget() {
  if (widgetLoaded) return;
  widgetLoaded = true;

  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = "https://iqada.site/widget/accessibility-widget.css";
  link.dataset.consent = "functional";
  document.head.appendChild(link);

  const script = document.createElement("script");
  script.src = "https://iqada.site/widget/accessibility-widget.js";
  script.dataset.consent = "functional";
  script.onload = () => {
    try {
      window.AccessibilityWidget?.init({ position: "bottom-left", theme: "light" });
    } catch {
      // ignore
    }
  };
  document.body.appendChild(script);
}

function applyConsent(c: StoredConsent | null) {
  initConsentMode();

  const analytics = c?.analytics ? "granted" : "denied";
  const functional = c?.functional ? "granted" : "denied";

  window.gtag!("consent", "update", {
    analytics_storage: analytics,
    functionality_storage: functional,
    personalization_storage: functional,
  });

  if (c?.analytics) loadGoogleAnalytics();
  if (c?.functional) loadAccessibilityWidget();
}

export function initConsentedScripts() {
  if (typeof window === "undefined") return;
  initConsentMode();
  applyConsent(getConsent());
  onConsentChange((c) => applyConsent(c));
}
